import {
    useCallback,
    useEffect,
    useRef,
    type ReactNode,
    type RefObject,
    type ComponentProps
} from 'react';
import { isEmpty } from 'lodash/fp.js';
import classnames from 'classnames/bind';
import { Defaults, Theme } from 'app/lib/constants';
import { useDefaultId } from 'app/lib/hooks';
import { ThemeProvider } from 'app/components/core/ThemeProvider';
import { ModalContext } from './ModalContext';
import { useStoredDialogSize } from './useStoredDialogSize';
import { useDialogDrag } from './useDialogDrag';
import * as styles from './Dialog.css';

interface DialogProps extends Omit<ComponentProps<'dialog'>, 'id' | 'children'> {
    className?: string;
    children?: ReactNode;
    id?: string;
    testId?: string;
    width?: number;
    height?: number;
    theme?: Theme;
    isResizable?: boolean;
    isMovable?: boolean;
    isNonModal?: boolean;
    onClose?: () => void;
}

const cx = classnames.bind(styles);
const DIALOG_MARGIN = 96;

function useNativeDialogToggle(ref: RefObject<HTMLDialogElement | null>, isNonModal: boolean) {
    useEffect(() => {
        const dialog = ref.current!;

        if (isNonModal) {
            dialog.show();
        } else {
            dialog.showModal();
        }

        return () => {
            dialog.close();
        };
    }, [ref, isNonModal]);
}

export function Dialog({
    className,
    children,
    id,
    testId,
    width,
    height,
    theme = Theme.LIGHT,
    isResizable = false,
    isMovable = false,
    isNonModal = false,
    onClose = Defaults.NOOP,
    ...props
}: DialogProps) {
    if ((isResizable || isMovable) && isEmpty(id)) {
        throw new Error(
            '[Dialog] resizable/movable dialogs must specify an `id` to store state in localstorage.'
        );
    }

    const ref = useRef<HTMLDialogElement>(null);
    const myId = useDefaultId('modal', id);
    const titleId = `${myId}-title`;

    const defaultSize = { width, height };
    const size = useStoredDialogSize(myId, ref, defaultSize, isResizable);

    const { translate, onPointerDown: onHeaderPointerDown } = useDialogDrag({
        id: myId,
        dialogRef: ref,
        enabled: isMovable
    });

    const rect = ref.current?.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxWidth = rect
        ? Math.min(vw - DIALOG_MARGIN, 2 * Math.min(rect.left, vw - rect.right) + rect.width)
        : vw - DIALOG_MARGIN;
    const maxHeight = rect
        ? Math.min(vh - DIALOG_MARGIN, 2 * Math.min(rect.top, vh - rect.bottom) + rect.height)
        : vh - DIALOG_MARGIN;

    const classes = cx(
        'base',
        `theme-${theme}`,
        { 'is-resizable': isResizable, 'is-non-modal': isNonModal },
        className
    );
    const style = {
        width: size.width,
        height: size.height,
        minWidth: width,
        minHeight: height,
        maxWidth,
        maxHeight,
        translate: isMovable ? `${translate.x}px ${translate.y}px` : undefined
    };

    useEffect(() => {
        const dialog = ref.current;
        if (!dialog || !isResizable) {
            return;
        }
        const rect = dialog.getBoundingClientRect();
        if (rect.width > maxWidth) {
            dialog.style.width = `${maxWidth}px`;
        }
        if (rect.height > maxHeight) {
            dialog.style.height = `${maxHeight}px`;
        }
    }, [isResizable, maxWidth, maxHeight]);

    useNativeDialogToggle(ref, isNonModal);

    const handleCancel = useCallback(() => {
        ref.current?.close();
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <dialog
                id={myId}
                className={classes}
                style={style}
                aria-labelledby={titleId}
                data-testid={testId ?? id}
                onCancel={handleCancel}
                onClose={onClose}
                {...props}
                ref={ref}>
                <ModalContext.Provider value={{ titleId, onClose, onHeaderPointerDown }}>
                    {children}
                </ModalContext.Provider>
            </dialog>
        </ThemeProvider>
    );
}
